import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileSignature, Save, Eye, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUrlField } from '@/components/admin/ImageUrlField';

const PLACEHOLDER_HINT = 'Placeholders: {{item_title}} {{brand}} {{category}} {{customer_name}} {{order_number}} {{date}} {{value}} {{intended_use}} {{condition}} {{age}}';

const TextArea = ({ value, onChange, rows = 3, testId }) => (
  <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows}
    className="w-full mt-1 p-2 rounded border border-stone text-xs resize-y focus:outline-none focus:ring-1 focus:ring-ring/30"
    data-testid={testId} />
);

const ListEditor = ({ label, items, onChange, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    {(items || []).map((p, i) => (
      <div key={i} className="flex items-start gap-1 mt-1">
        <textarea value={p} onChange={e => { const a = [...items]; a[i] = e.target.value; onChange(a); }} rows={2}
          className="flex-1 p-2 rounded border border-stone text-xs resize-y focus:outline-none" data-testid={`${testId}-${i}`} />
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 mt-1" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></Button>
      </div>
    ))}
    <Button variant="outline" size="sm" className="text-xs gap-1 mt-1" onClick={() => onChange([...(items || []), ''])}><Plus className="h-3 w-3" /> Add paragraph</Button>
  </div>
);

const useTemplate = (key) => {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get(`/admin/doc-templates/${key}`).then(r => setTpl(r.data)).catch(() => toast.error('Failed to load template'));
  }, [key]);

  const set = (field, value) => setTpl(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/doc-templates/${key}`, { value: tpl });
      setTpl(res.data);
      toast.success('Template saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(`/admin/doc-templates/${key}/preview`, { value: tpl });
      const bytes = Uint8Array.from(atob(res.data.pdf_base64), ch => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Preview failed'); }
    finally { setPreviewing(false); }
  };

  return { tpl, set, save, preview, saving, previewing };
};

const Toolbar = ({ save, preview, saving, previewing, testPrefix }) => (
  <div className="flex gap-2 justify-end">
    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={preview} disabled={previewing} data-testid={`${testPrefix}-preview-btn`}>
      {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Preview PDF
    </Button>
    <Button size="sm" className="h-7 text-xs gap-1" onClick={save} disabled={saving} data-testid={`${testPrefix}-save-btn`}>
      <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save Template'}
    </Button>
  </div>
);

const CounterfeitLetterEditor = () => {
  const { tpl, set, save, preview, saving, previewing } = useTemplate('counterfeit_letter');
  if (!tpl) return <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>;
  return (
    <div className="space-y-3" data-testid="counterfeit-letter-editor">
      <p className="text-[10px] text-muted-foreground">{PLACEHOLDER_HINT}. The letter renders the item's four evidence photos in squares at the bottom of the page.</p>
      <div><Label className="text-xs">Heading</Label><Input value={tpl.heading || ''} onChange={e => set('heading', e.target.value)} className="mt-1 h-8 text-xs" data-testid="cl-heading" /></div>
      <ListEditor label="Letter paragraphs" items={tpl.intro_paragraphs} onChange={v => set('intro_paragraphs', v)} testId="cl-paragraph" />
      <div><Label className="text-xs">Closing / disclaimer</Label><TextArea value={tpl.closing_paragraph} onChange={v => set('closing_paragraph', v)} testId="cl-closing" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Signature name</Label><Input value={tpl.signature_name || ''} onChange={e => set('signature_name', e.target.value)} className="mt-1 h-8 text-xs" data-testid="cl-sig-name" /></div>
        <div><Label className="text-xs">Signature title</Label><Input value={tpl.signature_title || ''} onChange={e => set('signature_title', e.target.value)} className="mt-1 h-8 text-xs" data-testid="cl-sig-title" /></div>
      </div>
      <Toolbar save={save} preview={preview} saving={saving} previewing={previewing} testPrefix="cl" />
    </div>
  );
};

const CertificateEditor = () => {
  const { tpl, set, save, preview, saving, previewing } = useTemplate('certificate');
  if (!tpl) return <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>;
  return (
    <div className="space-y-3" data-testid="certificate-editor">
      <p className="text-[10px] text-muted-foreground">Landscape Certificate of Authenticity. The item title, brand, evidence photos, cert code, QR and date are rendered automatically from the item — only the copy below is editable.</p>
      <div><Label className="text-xs">Title</Label><Input value={tpl.title || ''} onChange={e => set('title', e.target.value)} className="mt-1 h-8 text-xs" data-testid="coa-title" /></div>
      <div><Label className="text-xs">Authentication statement</Label><TextArea value={tpl.authentication_statement} onChange={v => set('authentication_statement', v)} rows={4} testId="coa-statement" /></div>
      <div><Label className="text-xs">Footer disclaimer</Label><TextArea value={tpl.disclaimer} onChange={v => set('disclaimer', v)} rows={3} testId="coa-disclaimer" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Signature name</Label><Input value={tpl.signature_name || ''} onChange={e => set('signature_name', e.target.value)} className="mt-1 h-8 text-xs" data-testid="coa-sig-name" /></div>
        <div><Label className="text-xs">Signature title</Label><Input value={tpl.signature_title || ''} onChange={e => set('signature_title', e.target.value)} className="mt-1 h-8 text-xs" data-testid="coa-sig-title" /></div>
      </div>
      <Toolbar save={save} preview={preview} saving={saving} previewing={previewing} testPrefix="coa" />
    </div>
  );
};

const AppraisalEditor = () => {
  const { tpl, set, save, preview, saving, previewing } = useTemplate('appraisal');
  const uploadKeyFromUrl = (url) => (url || '').replace(/^\/api\/public-media\//, '');
  if (!tpl) return <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>;
  const extras = tpl.extra_images || [];
  return (
    <div className="space-y-3" data-testid="appraisal-editor">
      <p className="text-[10px] text-muted-foreground">A4 format. {PLACEHOLDER_HINT}</p>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Intended use label (header)</Label><Input value={tpl.intended_use_label || ''} onChange={e => set('intended_use_label', e.target.value)} className="mt-1 h-8 text-xs" data-testid="ap-use-label" /></div>
        <div><Label className="text-xs">Comparable research heading</Label><Input value={tpl.comparable_research_heading || ''} onChange={e => set('comparable_research_heading', e.target.value)} className="mt-1 h-8 text-xs" /></div>
      </div>
      <div><Label className="text-xs">Market speculation (default copy)</Label><TextArea value={tpl.market_speculation} onChange={v => set('market_speculation', v)} testId="ap-speculation" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Letter heading</Label><Input value={tpl.letter_heading || ''} onChange={e => set('letter_heading', e.target.value)} className="mt-1 h-8 text-xs" data-testid="ap-letter-heading" /></div>
        <div><Label className="text-xs">Salutation</Label><Input value={tpl.letter_salutation || ''} onChange={e => set('letter_salutation', e.target.value)} className="mt-1 h-8 text-xs" /></div>
      </div>
      <ListEditor label="Letter paragraphs" items={tpl.letter_paragraphs} onChange={v => set('letter_paragraphs', v)} testId="ap-paragraph" />
      <div className="p-3 rounded-lg border border-stone/40 bg-stone/5 space-y-2" data-testid="ap-certification-section">
        <Label className="text-xs font-semibold">Certification (USPAP)</Label>
        <div><Label className="text-[10px] text-muted-foreground">Intro line</Label><TextArea value={tpl.certification_intro} onChange={v => set('certification_intro', v)} rows={2} /></div>
        <ListEditor label="Bullets" items={tpl.certification_bullets} onChange={v => set('certification_bullets', v)} testId="ap-bullet" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">Signature name</Label><Input value={tpl.signature_name || ''} onChange={e => set('signature_name', e.target.value)} className="mt-1 h-8 text-xs" data-testid="ap-sig-name" /></div>
        <div><Label className="text-xs">Signature title</Label><Input value={tpl.signature_title || ''} onChange={e => set('signature_title', e.target.value)} className="mt-1 h-8 text-xs" /></div>
        <div><Label className="text-xs">EIN</Label><Input value={tpl.ein || ''} onChange={e => set('ein', e.target.value)} className="mt-1 h-8 text-xs" data-testid="ap-ein" /></div>
      </div>
      <div>
        <Label className="text-xs">Signature image (optional)</Label>
        <div className="mt-1">
          <ImageUrlField value={tpl.signature_image_key ? `/api/public-media/${tpl.signature_image_key}` : ''}
            onChange={v => set('signature_image_key', uploadKeyFromUrl(v))}
            uploadPath="/admin/doc-templates/upload-image" testId="ap-signature-image" placeholder="Upload a signature image" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Additional document images</Label>
        <p className="text-[10px] text-muted-foreground mb-1">These render on an "Additional Photographs" page in every appraisal.</p>
        {extras.map((img, i) => (
          <div key={i} className="flex items-center gap-1 mt-1 p-2 rounded bg-stone/5 border border-stone/20">
            <div className="flex-1">
              <ImageUrlField value={img.key ? `/api/public-media/${img.key}` : ''}
                onChange={v => { const a = [...extras]; a[i] = { ...a[i], key: uploadKeyFromUrl(v) }; set('extra_images', a); }}
                uploadPath="/admin/doc-templates/upload-image" testId={`ap-extra-image-${i}`} />
            </div>
            <Input value={img.caption || ''} onChange={e => { const a = [...extras]; a[i] = { ...a[i], caption: e.target.value }; set('extra_images', a); }}
              className="h-7 text-xs w-40" placeholder="Caption" />
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => set('extra_images', extras.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="text-xs gap-1 mt-1" onClick={() => set('extra_images', [...extras, { key: '', caption: '' }])} data-testid="ap-add-image-btn"><Plus className="h-3 w-3" /> Add image</Button>
      </div>
      <div><Label className="text-xs">Footer text</Label><Input value={tpl.footer_text || ''} onChange={e => set('footer_text', e.target.value)} className="mt-1 h-8 text-xs" /></div>
      <Toolbar save={save} preview={preview} saving={saving} previewing={previewing} testPrefix="ap" />
    </div>
  );
};

export default function DocumentTemplatesPage() {
  return (
    <div data-testid="document-templates-page">
      <div className="flex items-center gap-2 mb-4">
        <FileSignature className="h-4 w-4 text-ink" />
        <h1 className="text-base font-semibold text-ink">Document Templates</h1>
      </div>
      <Card className="bg-white border-stone/50 max-w-3xl">
        <CardContent className="p-4">
          <Tabs defaultValue="counterfeit_letter">
            <TabsList className="mb-3">
              <TabsTrigger value="counterfeit_letter" className="text-xs" data-testid="tab-counterfeit-letter">Counterfeit Letter</TabsTrigger>
              <TabsTrigger value="appraisal" className="text-xs" data-testid="tab-appraisal">Appraisal Report (A4)</TabsTrigger>
              <TabsTrigger value="certificate" className="text-xs" data-testid="tab-certificate">Certificate of Authenticity</TabsTrigger>
            </TabsList>
            <TabsContent value="counterfeit_letter"><CounterfeitLetterEditor /></TabsContent>
            <TabsContent value="appraisal"><AppraisalEditor /></TabsContent>
            <TabsContent value="certificate"><CertificateEditor /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
